// ============================================================
// Key Moment Extraction — Task 1.4
// Detects eval swings, labels move quality, detects tactics,
// and identifies the single most critical moment.
// ============================================================

import { Chess, type Square } from "chess.js"
import type { MoveData, KeyMoment, TacticType } from "@shared/types"

// --- Constants ---

/** Minimum eval swing (centipawns) to be considered a key moment */
const KEY_MOMENT_THRESHOLD = 150

/** Eval swing thresholds for quality labels */
const BLUNDER_THRESHOLD = 200
const MISTAKE_THRESHOLD = 100
const INACCURACY_THRESHOLD = 50

/** Maximum number of key moments to include */
const MAX_KEY_MOMENTS = 8

// --- Move Quality ---

export type MoveQuality = KeyMoment["quality"]

/**
 * Label the quality of a move based on eval delta.
 * evalDelta is ALWAYS from White's perspective:
 * - Positive = position improved for White
 * - Negative = position improved for Black
 *
 * We normalize to the mover's perspective internally:
 * - White move + negative delta = White blundered
 * - Black move + positive delta = Black blundered (White gained)
 */
export function labelMoveQuality(
  evalDelta: number,
  moveIndex: number,
  color: "white" | "black"
): MoveQuality {
  // First few moves are "book" — no quality judgment
  if (moveIndex < 6) return "book"

  // Normalize to mover's perspective:
  // For White, positive delta = good. For Black, negative delta = good.
  const moverDelta = color === "white" ? evalDelta : -evalDelta

  // Positive moverDelta = the mover improved their position
  if (moverDelta > BLUNDER_THRESHOLD) return "brilliant"
  if (moverDelta > INACCURACY_THRESHOLD) return "good"

  // Negative moverDelta = the mover worsened their position
  if (moverDelta < -BLUNDER_THRESHOLD) return "blunder"
  if (moverDelta < -MISTAKE_THRESHOLD) return "mistake"
  if (moverDelta < -INACCURACY_THRESHOLD) return "inaccuracy"

  return "good"
}

// --- Tactic Detection ---

/**
 * Basic tactic detection using chess.js position analysis.
 * This is heuristic — not full tactical analysis, but covers
 * the most common patterns.
 */
export function detectTactic(
  move: MoveData,
  _fenBefore: string,
  fenAfter: string
): TacticType | null {
  try {
    const afterBoard = new Chess(fenAfter)

    // Detect promotion
    if (move.promotion) return "promotion"

    // Detect back rank mate threat
    if (move.san.includes("#")) {
      const matedKingSq = findKing(afterBoard, move.color === "white" ? "b" : "w")
      if (matedKingSq) {
        const rank = matedKingSq[1]
        if (rank === "1" || rank === "8") return "back_rank"
      }
    }

    // Detect discovered attack / double check
    if (move.san.includes("+")) {
      // If the piece that moved isn't naturally a checking piece from its square,
      // this might be a discovered attack
      const checkingPieces = getCheckingPieces(afterBoard)
      if (checkingPieces.length >= 2) return "double_check"
      if (
        checkingPieces.length === 1 &&
        checkingPieces[0] !== move.to
      ) {
        return "discovered_attack"
      }
    }

    // Detect fork: after the move, the moved piece attacks 2+ valuable pieces
    const attackedSquares = getAttackedPieces(afterBoard, move.to, move.color)
    const valuableAttacked = attackedSquares.filter((sq) => {
      const target = afterBoard.get(sq as Square)
      return target && target.type !== "p" // attacking non-pawns
    })
    if (valuableAttacked.length >= 2) return "fork"

    // Detect sacrifice: the move captures less material than it gives up
    // or puts a piece on a square where it can be captured for free
    if (!move.captured && move.piece !== "pawn") {
      const canBeCaptured = isSquareAttacked(
        afterBoard,
        move.to,
        move.color === "white" ? "black" : "white"
      )
      // Normalize to mover's perspective — sacrifice means the mover
      // improved their position despite putting a piece en prise
      const moverDelta = move.color === "white" ? move.evalDelta : -move.evalDelta
      if (canBeCaptured && moverDelta > INACCURACY_THRESHOLD) {
        return "sacrifice"
      }
    }

    // Detect pin: check if a piece is pinned after the move
    // (simplified: if a piece is between the king and an attacker)
    // This is complex to detect accurately, so we keep it simple
    if (isPinDetectable(afterBoard, move)) return "pin"

    return null
  } catch {
    return null
  }
}

// --- Chess position analysis helpers ---

function findKing(
  chess: Chess,
  color: "w" | "b"
): string | null {
  const board = chess.board()
  for (let rank = 0; rank < 8; rank++) {
    for (let file = 0; file < 8; file++) {
      const piece = board[rank][file]
      if (piece && piece.type === "k" && piece.color === color) {
        const fileChar = String.fromCharCode(97 + file)
        const rankNum = 8 - rank
        return `${fileChar}${rankNum}`
      }
    }
  }
  return null
}

function getCheckingPieces(chess: Chess): string[] {
  // Find which pieces are giving check
  const inCheck = chess.inCheck()
  if (!inCheck) return []

  const turn = chess.turn() // side that is IN check
  const kingSquare = findKing(chess, turn)
  if (!kingSquare) return []

  const attackerColor = turn === "w" ? "b" : "w"
  const checkers: string[] = []

  // Check all opponent pieces to see which ones attack the king
  const board = chess.board()
  for (let rank = 0; rank < 8; rank++) {
    for (let file = 0; file < 8; file++) {
      const piece = board[rank][file]
      if (piece && piece.color === attackerColor) {
        const sq = `${String.fromCharCode(97 + file)}${8 - rank}`
        if (canAttackSquare(chess, sq, kingSquare, piece.type)) {
          checkers.push(sq)
        }
      }
    }
  }

  return checkers
}

function canAttackSquare(
  _chess: Chess,
  from: string,
  to: string,
  pieceType: string
): boolean {
  // Simple geometric check for whether a piece type can attack a square
  const fromFile = from.charCodeAt(0) - 97
  const fromRank = parseInt(from[1]) - 1
  const toFile = to.charCodeAt(0) - 97
  const toRank = parseInt(to[1]) - 1

  const dx = Math.abs(toFile - fromFile)
  const dy = Math.abs(toRank - fromRank)

  switch (pieceType) {
    case "n":
      return (dx === 1 && dy === 2) || (dx === 2 && dy === 1)
    case "b":
      return dx === dy
    case "r":
      return dx === 0 || dy === 0
    case "q":
      return dx === dy || dx === 0 || dy === 0
    case "p":
      return dx === 1 && dy === 1
    case "k":
      return dx <= 1 && dy <= 1
    default:
      return false
  }
}

function getAttackedPieces(
  chess: Chess,
  fromSquare: string,
  color: "white" | "black"
): string[] {
  const attacked: string[] = []
  const board = chess.board()
  const opponentColor = color === "white" ? "b" : "w"

  const piece = chess.get(fromSquare as any)
  if (!piece) return attacked

  for (let rank = 0; rank < 8; rank++) {
    for (let file = 0; file < 8; file++) {
      const target = board[rank][file]
      if (target && target.color === opponentColor) {
        const sq = `${String.fromCharCode(97 + file)}${8 - rank}`
        if (canAttackSquare(chess, fromSquare, sq, piece.type)) {
          attacked.push(sq)
        }
      }
    }
  }

  return attacked
}

function isSquareAttacked(
  chess: Chess,
  square: string,
  byColor: "white" | "black"
): boolean {
  const c = byColor === "white" ? "w" : "b"
  return chess.isAttacked(square as Square, c)
}

function isPinDetectable(chess: Chess, move: MoveData): boolean {
  // Simplified pin detection: if a sliding piece (bishop, rook, queen)
  // moves and creates a line to the opponent's king through another piece
  if (move.piece !== "bishop" && move.piece !== "rook" && move.piece !== "queen") {
    return false
  }

  // Very basic: check if the moved piece is on the same line as the opponent king
  const opponentColor = move.color === "white" ? "b" : "w"
  const kingSquare = findKing(chess, opponentColor as "w" | "b")
  if (!kingSquare) return false

  const pieceFile = move.to.charCodeAt(0) - 97
  const pieceRank = parseInt(move.to[1]) - 1
  const kingFile = kingSquare.charCodeAt(0) - 97
  const kingRank = parseInt(kingSquare[1]) - 1

  const dx = Math.abs(pieceFile - kingFile)
  const dy = Math.abs(pieceRank - kingRank)

  // On same diagonal, rank, or file
  return dx === dy || dx === 0 || dy === 0
}

// --- Key Moment Description ---

function describeMove(move: MoveData, quality: MoveQuality): string {
  const side = move.color === "white" ? "White" : "Black"

  if (quality === "blunder") {
    if (move.captured) {
      return `${side}'s ${move.piece} captures on ${move.to}, but this is a critical mistake.`
    }
    return `${side} plays ${move.piece} to ${move.to} — a costly error.`
  }

  if (quality === "brilliant") {
    if (move.captured) {
      return `${side}'s ${move.piece} captures on ${move.to} — a brilliant tactical shot.`
    }
    return `${side} finds a stunning ${move.piece} move to ${move.to}.`
  }

  if (quality === "mistake") {
    return `${side}'s ${move.piece} move to ${move.to} is inaccurate.`
  }

  if (move.captured) {
    return `${side}'s ${move.piece} captures on ${move.to}.`
  }

  return `${side} plays ${move.piece} to ${move.to}.`
}

function describeBestMove(move: MoveData): string | null {
  if (!move.bestMove) return null
  // We don't have the best move's full detail, so keep it generic
  return `A stronger continuation was available.`
}

// --- Public API ---

export interface KeyMomentResult {
  keyMoments: KeyMoment[]
  criticalMoveIndex: number
}

/**
 * Extract key moments from a list of evaluated moves.
 * Returns the top moments sorted by importance and the index
 * of the single most critical moment (for OG image).
 */
export function extractKeyMoments(moves: MoveData[]): KeyMomentResult {
  if (moves.length === 0) {
    return { keyMoments: [], criticalMoveIndex: 0 }
  }

  const candidates: (KeyMoment & { absSwing: number })[] = []

  for (let i = 1; i < moves.length; i++) {
    const move = moves[i]
    const prevMove = moves[i - 1]
    const absSwing = Math.abs(move.evalDelta)

    if (absSwing < KEY_MOMENT_THRESHOLD && i >= 6) continue

    const quality = labelMoveQuality(move.evalDelta, i, move.color)
    if (quality === "book" || quality === "good") continue

    // Get FEN before this move (the previous position)
    const fenBefore = prevMove.fen
    const fenAfter = move.fen

    const tacticType = detectTactic(move, fenBefore, fenAfter)

    const importance: KeyMoment["importance"] =
      absSwing >= 300
        ? "critical"
        : absSwing >= 200
          ? "high"
          : "moderate"

    candidates.push({
      moveNumber: move.moveNumber,
      color: move.color,
      moveSan: move.san,
      piece: move.piece,
      fromSquare: move.from,
      toSquare: move.to,
      evalBefore: prevMove.eval,
      evalAfter: move.eval,
      evalSwing: move.evalDelta,
      quality,
      bestMoveSan: move.bestMove,
      bestMoveDescription: describeBestMove(move),
      tacticType,
      importance,
      description: describeMove(move, quality),
      absSwing,
    })
  }

  // Sort by absolute swing magnitude (biggest first)
  candidates.sort((a, b) => b.absSwing - a.absSwing)

  // Take top N moments
  const topMoments = candidates.slice(0, MAX_KEY_MOMENTS)

  // Sort by move number for chronological order
  topMoments.sort((a, b) => a.moveNumber - b.moveNumber)

  // Find the single most critical moment (biggest absolute swing)
  let criticalMoveIndex = 0
  let maxSwing = 0
  for (let i = 0; i < moves.length; i++) {
    const absSwing = Math.abs(moves[i].evalDelta)
    if (absSwing > maxSwing) {
      maxSwing = absSwing
      criticalMoveIndex = i
    }
  }

  // Remove the absSwing helper field from output
  const keyMoments: KeyMoment[] = topMoments.map(
    ({ absSwing: _, ...moment }) => moment
  )

  return { keyMoments, criticalMoveIndex }
}
